import { useRouter } from 'next/router';
import { useState } from 'react';
import styled from '@emotion/styled';
import { Toast } from '@components/_atoms/Toast';
import { useTranslation } from 'next-i18next';

// components
import Button from '@components/_atoms/Button';
import TextInputBox from '@components/_molecules/TextInputBox';
import DefaultButtonBox from '@components/_molecules/DefaultButtonBox';

// hooks
import { useResetPasswordFetch } from '@hooks/fetch/useAuthFetch';

// constants
import { MOBILE_MODE } from '@constants/size';
import { TextInputVariant } from '@constants/atoms';
import { isValidatePassword } from '@utils/regex';
import { FIND_COMPLETE } from '@constants/routes/routes';

// type
import { ResetFormType } from './types';
import ButtonCode from '@constants/log/buttonCode';

const Layout = styled.form``;

const FormWrapper = styled.div`
  margin-bottom: 438px;

  display: grid;
  row-gap: 24px;

  @media ${MOBILE_MODE} {
    margin-bottom: 156px;
  }
`;

export default function ResetContent() {
  const { t } = useTranslation('auth');

  const router = useRouter();

  const [form, setForm] = useState<ResetFormType>({
    password: '',
    passwordConfirm: '',
  });
  const [errorMessage, setErrorMessage] = useState<ResetFormType>({
    password: '',
    passwordConfirm: '',
  });

  const disabled =
    !form.password ||
    !!errorMessage.password ||
    !form.passwordConfirm ||
    !!errorMessage.passwordConfirm ||
    form.password !== form.passwordConfirm;

  const { resetPasswordFetchMutate } = useResetPasswordFetch();

  // form change
  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    checkValidation(name, value);
  };

  // form validation
  const checkValidation = (name: string, value: string) => {
    let message = '';
    if (name === 'password' && !isValidatePassword(value)) {
      message = t('find.reset.password-error-message');
    }
    if (name === 'passwordConfirm' && value !== form.password) {
      message = t('find.reset.passwordConfirm-error-message');
    }

    setErrorMessage((prev) => ({
      ...prev,
      [name]: message,
    }));
  };

  // 비밀번호 재설정 form submit
  const handleFormSubmit = () => {
    const { t: code } = router.query;
    if (!code) {
      Toast.error(t('find.reset.submit-error-message'));
      return;
    }

    if (typeof code === 'string') {
      resetPasswordFetchMutate(
        { t: code, password: form.password },
        {
          onSuccess: () => {
            router.replace(FIND_COMPLETE);
          },
        },
      );
    }
  };

  return (
    <Layout
      onSubmit={(e) => {
        e.preventDefault();
        handleFormSubmit();
      }}>
      <FormWrapper>
        <TextInputBox
          title={t('find.reset.password-title')}
          subTitle={t('find.reset.password-subTitle')}
          textInput={{
            variant: errorMessage.password ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
            type: 'password',
            name: 'password',
            value: form.password,
            onChange: handleFormChange,
            placeholder: t('find.reset.password-placeholder'),
          }}
          errorMessage={errorMessage.password}
        />
        <TextInputBox
          title={t('find.reset.passwordConfirm-title')}
          textInput={{
            variant: errorMessage.passwordConfirm ? TextInputVariant.ERROR : TextInputVariant.DEFAULT,
            type: 'password',
            name: 'passwordConfirm',
            value: form.passwordConfirm,
            onChange: handleFormChange,
            placeholder: t('find.reset.passwordConfirm-placeholder'),
          }}
          errorMessage={errorMessage.passwordConfirm}
        />
      </FormWrapper>

      <DefaultButtonBox
        button1={
          <Button disabled={disabled} text={t('find.reset.submit-button')} steelbosoId={ButtonCode.BTN_SUBMIT} />
        }
      />
    </Layout>
  );
}
